import { Fragment, useState } from 'react';
import { formatDateTime } from '../../../utils/api';
import { EngineChips } from './EngineReadBlock.jsx';

// Claude's structured verdict (spec docs/specs_20261008_verdict_notes.md), styled like the
// "Review done" banner. The accent follows the action button the verdict points at;
// a Human review takes the colour of the verdict it suggests.
// The Engine column is LIVE: the item's current engine signals, keyed by quote_item_id, not
// what the run saw when it saved the note (that stays in the evidence text).
const LABEL = {
    quote_ready: 'Quote Ready', consignment: 'Consignment', reject: 'Reject',
    more_info: 'More Info', fair_price: 'Fair price', duplicate: 'Duplicate', human_review: 'Human review',
};
const CONF_BADGE = { HIGH: 'badge badge-success', MEDIUM: 'badge badge-warning', LOW: 'badge badge-error' };
const SP_CHECK = { fine: 'fine', bug_high: 'SP bug, too high', bug_low: 'SP bug, too low' };
// Lucide circle-alert, inline like recordsIcons.jsx (the frontend has no icon package).
// aria-hidden: the chip beside it carries the meaning.
const AlertIcon = () => (
    <svg className="qa-vn-alert" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><line x1="12" x2="12" y1="8" y2="12" /><line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
);
const money = (n) => `£${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;

function Note({ note, signalsById }) {
    const [open, setOpen] = useState(null);
    const review = note.verdict === 'human_review';
    const accent = review ? note.suggested_verdict : note.verdict;
    // SP and BP are per unit; the totals are what the offer adds up to.
    const qty = (it) => it.qty ?? 1;
    const total = (k) => note.items.reduce((s, it) => s + Number(it[k]) * qty(it), 0);
    return (
        <div className={`qa-vn qa-vn--${accent}${review ? ' qa-vn--review' : ''}`}>
            <span className="qa-vn-hd">🤖 Verdict note · {note.created_by_name || '—'} · {formatDateTime(note.created_at)}</span>
            <div className="qa-vn-verdict">
                {review
                    ? <><AlertIcon /><span className="qa-vn-chip">Human review</span><strong>Suggested: {LABEL[note.suggested_verdict]}</strong></>
                    : <strong>{LABEL[note.verdict]}</strong>}
            </div>
            <div className="qa-vn-reason">{note.reason}</div>
            <div className="qa-vn-totals">Total SP {money(total('sp'))} · Total BP {money(total('bp'))}</div>
            {note.more_info_text && (
                <div className="qa-vn-quote"><div className="qa-lab">More Info text</div><div>{note.more_info_text}</div></div>
            )}
            {note.rejection_text && (
                <div className="qa-vn-quote"><div className="qa-lab">Rejection text</div><div>{note.rejection_text}</div></div>
            )}
            {note.offer_text && (
                <div className="qa-vn-quote"><div className="qa-lab">Message to the customer</div><div>{note.offer_text}</div></div>
            )}
            <table className="qa-vn-items">
                <thead><tr><th>Item</th><th>Identity</th><th>Grade</th><th>Qty</th><th>SP each</th><th>BP each</th><th>Engine</th><th>ID conf</th><th>SP conf</th><th>SP check</th></tr></thead>
                <tbody>
                    {note.items.map((it, i) => (<Fragment key={it.quote_item_id}>
                        <tr className="qa-vn-row" onClick={() => setOpen(open === i ? null : i)} title="Show the evidence">
                            <td>{it.name}</td><td className="qa-vn-id">{it.identity_tag}</td><td>{it.grade}</td><td>{qty(it)}</td>
                            <td>{money(it.sp)}</td><td>{money(it.bp)}</td>
                            <td><EngineChips signals={signalsById[it.quote_item_id]} /></td>
                            <td><span className={CONF_BADGE[it.id_confidence]}>{it.id_confidence}</span></td>
                            <td><span className={CONF_BADGE[it.sp_confidence]}>{it.sp_confidence}</span></td>
                            <td>{SP_CHECK[it.sp_check] || it.sp_check}</td>
                        </tr>
                        {open === i && (
                            <tr className="qa-vn-ev">
                                <td colSpan={10}>{it.demand ? `Demand ${it.demand}. ` : ''}{it.evidence}</td>
                            </tr>
                        )}
                    </Fragment>))}
                </tbody>
            </table>
            <details className="qa-vn-more">
                <summary>Customer note, done and not done</summary>
                <p><span className="qa-lab">Customer note</span> {note.customer_note || 'None.'}</p>
                <div className="qa-lab">Done</div>
                <ul>{note.done.map((d, i) => <li key={i}>{d}</li>)}</ul>
                <div className="qa-lab">Not done</div>
                {note.not_done.length ? <ul>{note.not_done.map((d, i) => <li key={i}>{d}</li>)}</ul> : <p className="qa-muted">Nothing.</p>}
            </details>
        </div>
    );
}

export default function VerdictNotePanel({ notes, error, signalsById = {} }) {
    const [showOlder, setShowOlder] = useState(false);
    if (error) return <div className="qa-error qa-banner" role="alert">Verdict notes failed to load ({error})</div>;
    if (!notes.length) return null;
    const [latest, ...older] = notes;
    return (
        <div className="qa-vn-wrap">
            <Note note={latest} signalsById={signalsById} />
            {older.length > 0 && (
                <button className="qa-rule-jump" onClick={() => setShowOlder(s => !s)}>
                    {showOlder ? 'Hide earlier verdicts' : `${older.length} earlier verdict${older.length === 1 ? '' : 's'}`}
                </button>
            )}
            {showOlder && older.map(n => <Note key={n.id} note={n} signalsById={signalsById} />)}
        </div>
    );
}
